import { AuthUser } from '@/types/auth';

/**
 * Utility functions for consistent user ID handling across the application
 * This helps prevent localStorage key accumulation due to inconsistent user ID sources
 */

/**
 * Gets the consistent user ID from various sources, with fallback priority
 * @param user - Authenticated user (primary source)
 * @param profile - Profile data from backend (fallback)
 * @returns Consistent user ID string
 */
export function getConsistentUserId(user?: AuthUser | null, profile?: { user_id?: string } | null): string | null {
  // Primary source: Authenticated user ID
  if (user?.id) {
    return user.id;
  }
  
  // Fallback: Profile user_id from backend
  if (profile?.user_id) {
    return profile.user_id;
  }
  
  return null;
}

/**
 * Cleans up old profile completion dismissed keys for a specific user
 * Removes all keys except the one for the current user ID
 * @param currentUserId - The current user's ID to keep
 */
export function cleanupProfileCompletionKeys(currentUserId: string | null): void {
  if (!currentUserId) return;
  
  try {
    const keysToRemove: string[] = [];
    const currentKey = `profile_completion_dismissed_${currentUserId}`;
    
    // Find all profile completion dismissed keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('profile_completion_dismissed_') && key !== currentKey) {
        keysToRemove.push(key);
      }
    }
    
    // Remove old keys
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      console.log(`Cleaned up old profile completion key: ${key}`);
    });
    
    if (keysToRemove.length > 0) {
      console.log(`Cleaned up ${keysToRemove.length} old profile completion dismissal keys`);
    }
  } catch (error) {
    console.error('Error cleaning up profile completion keys:', error);
  }
}

/**
 * Cleans up all user-specific localStorage keys for old users
 * @param currentUserId - The current user's ID to keep
 */
export function cleanupAllUserKeys(currentUserId: string | null): void {
  if (!currentUserId) return;
  
  try {
    const keysToRemove: string[] = [];
    const userKeyPatterns = [
      'profile_completion_dismissed_',
      'assessment_completed_',
      'assessment_skipped_'
    ];
    
    // Find all user-specific keys that don't belong to current user
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        for (const pattern of userKeyPatterns) {
          if (key.startsWith(pattern) && !key.endsWith(currentUserId)) {
            keysToRemove.push(key);
            break; // Don't check other patterns for this key
          }
        }
      }
    }
    
    // Remove old keys
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });
    
    if (keysToRemove.length > 0) {
      console.log(`Cleaned up ${keysToRemove.length} old user-specific localStorage keys for previous sessions`);
    }
  } catch (error) {
    console.error('Error cleaning up user localStorage keys:', error);
  }
}

/**
 * Gets a profile completion dismissed key for the current user
 * @param user - Authenticated user
 * @param profile - Profile data from backend
 * @returns localStorage key string or null if no user ID available
 */
export function getProfileCompletionKey(user?: AuthUser | null, profile?: { user_id?: string } | null): string | null {
  const userId = getConsistentUserId(user, profile);
  return userId ? `profile_completion_dismissed_${userId}` : null;
}

/**
 * Debug function to log all user-related localStorage keys
 */
export function debugUserLocalStorageKeys(): void {
  console.group('🔍 User LocalStorage Keys Debug');
  
  const userKeys: { key: string; pattern: string }[] = [];
  const patterns = [
    'profile_completion_dismissed_',
    'assessment_completed_',
    'assessment_skipped_',
    'ace-auth',
    'profile_data',
    'profile_strength'
  ];
  
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key) {
      for (const pattern of patterns) {
        if (key.includes(pattern)) {
          userKeys.push({ key, pattern });
          break;
        }
      }
    }
  }
  
  if (userKeys.length === 0) {
    console.log('No user-related localStorage keys found');
  } else {
    console.table(userKeys);
  }
  
  console.groupEnd();
}
